import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Box, Card, CardActionArea, CardContent, Typography, Stack, Avatar, Alert, Skeleton,
} from "@mui/material";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useTenant } from "../context/TenantContext";
import { PageHeader } from "../components/PageHeader";
import { useApi } from "../hooks/useApi";
import { iconFor } from "../config/icons";

interface DashboardItem {
  id: string; label: string; icon: string; color: string; desc: string;
  locked_reason?: string;
}
interface DashboardsResponse {
  tenant: string; available: DashboardItem[]; locked: DashboardItem[];
  stale: boolean; error?: string;
}

function friendlyLockReason(reason?: string): string {
  if (!reason) return "Bloqueado";
  if (reason === "not_configured_for_tenant") return "Não configurado para este tenant";
  if (reason.startsWith("missing_tables:")) return `Faltam tabelas: ${reason.split(":")[1]}`;
  if (reason.startsWith("tabelas_ausentes:")) return `Faltam tabelas: ${reason.split(":")[1]}`;
  if (reason === "sem_base") return "Cliente ainda sem base publicada";
  return reason;
}

export function Dashboards() {
  const { tenant } = useTenant();
  const api = useApi();
  const q = useQuery({
    queryKey: ["dashboards-catalog", tenant],
    queryFn: () => api.call<DashboardsResponse>("/api/dashboards"),
    enabled: !!tenant, staleTime: 60_000,
  });

  const available = q.data?.available ?? [];
  const locked = q.data?.locked ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Análises"
        title="Dashboards"
        subtitle={<>{available.length} disponíveis para <strong>{tenant ?? "?"}</strong>{locked.length > 0 && ` · ${locked.length} bloqueados`}</>}
      />

      {q.data?.stale && (
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          Catálogo em cache ou fora do ar ({q.data.error ?? "unknown"}).
        </Alert>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
        {q.isLoading && Array.from({ length: 3 }).map((_, i) => (
          <Card key={i} variant="outlined" sx={{ p: 2 }}>
            <Skeleton variant="circular" width={44} height={44} />
            <Skeleton width="60%" sx={{ mt: 1 }} />
            <Skeleton width="80%" />
          </Card>
        ))}
        {!q.isLoading && available.length === 0 && (
          <Card variant="outlined" sx={{ p: 2, gridColumn: "1 / -1" }}>
            <Typography variant="body2" color="text.secondary">
              Nenhum dashboard configurado para <strong>{tenant}</strong>.
            </Typography>
          </Card>
        )}
        {available.map((d) => {
          const Icon = iconFor(d.icon);
          return (
            <Card key={d.id} sx={{ "&:hover": { transform: "translateY(-2px)", boxShadow: "0 12px 24px rgba(2,26,44,0.10)" } }}>
              <CardActionArea component={RouterLink} to={`/dashboards/${d.id}`} data-testid={`dashboard-${d.id}`}>
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                    <Avatar sx={{ bgcolor: d.color, width: 44, height: 44 }}>
                      <Icon />
                    </Avatar>
                    <Typography variant="h6" sx={{ color: "primary.dark", flex: 1 }}>{d.label}</Typography>
                    <ArrowForwardRoundedIcon sx={{ color: "primary.main" }} />
                  </Stack>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>{d.desc}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>

      {locked.length > 0 && (
        <>
          <PageHeader title="Não disponíveis" subtitle="Requerem configuração adicional para este tenant." />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 2 }}>
            {locked.map((d) => {
              const Icon = iconFor(d.icon);
              return (
                <Card key={d.id} variant="outlined"
                  sx={{ opacity: 0.55, borderStyle: "dashed", cursor: "not-allowed" }}>
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                      <Avatar sx={{ bgcolor: "grey.300", width: 40, height: 40, color: "text.secondary" }}>
                        <Icon />
                      </Avatar>
                      <Typography variant="h6" sx={{ color: "text.secondary", flex: 1 }}>{d.label}</Typography>
                      <LockRoundedIcon sx={{ color: "text.secondary" }} fontSize="small" />
                    </Stack>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {friendlyLockReason(d.locked_reason)}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </>
      )}
    </>
  );
}
